import { makeAutoObservable } from 'mobx';
import { injectable } from 'inversify';
import { clearPersistedStore, makePersistable } from 'mobx-persist-store';
import { notifications } from '@shared/utils/notifications';
import { HistoryModule } from '@mobx/services/history';
import logger from 'js-logger';
import { PATH_AUTH, PATH_HOME } from '@app/routes/paths';
import { type AuthService, type UserData } from '@shared/types/mobx/services/auth';
import { queryClient } from '@shared/api/reactQuery';
import { getPostApiAuthMutationOptions } from '@/shared/api/orval/auth/auth';
import { type ResponsesUserResponse } from '@/shared/api/orval/models';
import { getGetApiUsersQueryOptions } from '@/shared/api/orval/users/users';

@injectable()
export class AuthServiceImpl implements AuthService {
  id: number | null = null;

  userData: UserData | ResponsesUserResponse | undefined;

  constructor(private historyModule: HistoryModule) {
    makeAutoObservable(this);
    makePersistable(this, { name: 'AuthModule', properties: ['id', 'userData'] });
  }

  setAuthData = async (data: { userId: number }) => {
    if (!data.userId) notifications.noAuth();

    this.id = data.userId;
    await this.loadUserData();
  };

  authUser = async (phone: string, code: string): Promise<void> => {
    try {
      const mutation = queryClient.getMutationCache().build(queryClient, getPostApiAuthMutationOptions());
      const response = await mutation.execute({ params: { username: phone, password: code } });

      if (!response.result?.userId) {
        notifications.noAuth();
        return;
      }

      this.id = response.result.userId;
      await this.loadUserData();

      this.historyModule.goToSelectRoles();
    } catch (e) {
      console.error(e);
      notifications.error(e);
    }
  };

  authUserAfterRegistration = async (userId: number): Promise<void> => {
    try {
      this.id = userId;
      await this.loadUserData();

      this.historyModule.goToCabinet();
    } catch (e) {
      logger.error(e);
      notifications.error();
    }
  };

  sessionExpired = () => {
    this.id = null;
    this.userData = undefined;
    localStorage.setItem('token', '');

    const path = window.location.toString();

    const notRedirectAuthPaths = [PATH_AUTH.checkContact, PATH_AUTH.login, PATH_AUTH.register];

    const notRedirectLocations = [...notRedirectAuthPaths];
    const isNotRedirectLocation = () => notRedirectLocations.some((allowed) => path.includes(allowed));
    const isHomeLocation = () => window.location.pathname === PATH_HOME.root;

    if (isNotRedirectLocation() || isHomeLocation()) return;

    this.historyModule.goToCheckContact();
  };

  logout = () => {
    this.id = null;
    this.userData = undefined;
    localStorage.setItem('token', '');

    this.historyModule.goToHome();
  };

  // Язык, ПРИНЯТЫЙ СЕРВЕРОМ, кладём в данные текущего пользователя — чтобы персист в localStorage
  // и следующая загрузка страницы начинались с актуального значения. Без этого при перезагрузке
  // кабинет секунду показывал бы прежний язык (из персиста), а потом переключался на новый
  // (из ответа /api/Users) — то самое мигание, которого быть не должно.
  // Вызывается ТОЛЬКО после успешного ответа сервера: до него в userData лежит то, что сервер
  // действительно хранит, и подменять это оптимистично нельзя.
  setUserLang = (lang: string) => {
    if (!this.userData) return;

    this.userData = { ...this.userData, lang };
  };

  refreshUserInfo = async () => {
    try {
      await this.loadUserData();
    } catch (e: any) {
      logger.error(e);
    }
  };

  // Чистим persist сессии: сначала сбрасываем поля к initial, затем удаляем localStorage-ключ.
  // Иначе старые id/userData протекают между входами и роль «застревает» на прежней.
  clearSession = async () => {
    this.id = null;
    this.userData = undefined;
    await clearPersistedStore(this);
  };

  private async loadUserData() {
    try {
      const response = await queryClient.fetchQuery(getGetApiUsersQueryOptions({ query: { gcTime: 0, staleTime: 0 } }));
      if (response.code !== 0 || !response.result) {
        throw new Error(response.message ?? 'common:unknownError');
      }

      this.userData = response.result;
    } catch (e) {
      notifications.error(e);
    }
  }
}
